import '@fontsource/poppins';
import { Icon } from '@iconify/react';
import axios from 'axios';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';

import React, { useEffect, useState } from 'react';

// eslint-disable-next-line import/no-extraneous-dependencies
import ReactPlayer from 'react-player/lazy';

import { useAuth } from '@src/contexts/authContext';

import { uuid } from '@utils';
import * as tusUtils from '@utils/tus';

const url = `${process.env.REACT_APP_API_URL}/event-tag-items`;

const options = [
  { value: 'doctor', label: 'Doctors' },
  { value: 'clinic_assistants', label: 'Clinic Assistants' },
  { value: 'nurse', label: 'Nurses' },
  { value: 'personal.specialist', label: 'Hub-Specialist' },
  { value: 'personal.allied-healthcare', label: 'Hub-Allied Healthcare' },
  { value: 'org.healthcare', label: 'Hub-Organisations' },
  { value: 'personal.gp', label: 'Hub-GP Locum' },
  // { value: 'ALL', label: 'ALL' },
];

function CheckboxGroup({ selectedValues = [], onChange }) {
  return (
    <div className='text-md py-2 px-2 text-gray-600'>
      <FormGroup>
        {options.map((option) => (
          <FormControlLabel
            key={option.value}
            control={
              <Checkbox
                className='py-1'
                style={{ color: '#1788b9' }}
                checked={selectedValues.includes(option.value)}
                onChange={(e) => onChange(option.value, e.target.checked)}
              />
            }
            label={option.label}
          />
        ))}
      </FormGroup>
    </div>
  );
}

export default function CreateListedEventForm({
  formInput,
  setFormInput,
  update,
  setUpdate,
  setFileTus,
  onTusResume,
  setIsLoading,
}) {
  const { auth } = useAuth();
  const videoPlayerRef = React.useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [previewVideo, setPreviewVideo] = useState('');
  const [isVideo, setIsVideo] = useState(false);
  const [videoPlayerId, setVideoPlayerId] = useState(uuid());
  const [isReloadVideoPlayer, setIsReloadVideoPlayer] = useState(false);
  const [isVideoPlayerReloaded, setIsVideoPlayerReloaded] = useState(false);
  const videoUrl = React.useMemo(
    () => previewVideo || formInput?.learning_file,
    [previewVideo, formInput],
  );
  const showVideoPreview = React.useMemo(
    () => selectedVideo || previewVideo || formInput?.learning_file,
    [selectedVideo, previewVideo, formInput?.learning_file],
  );
  const showVideoPlayer = React.useMemo(
    () => (selectedVideo && isVideo) || formInput?.learning_file,
    [selectedVideo, isVideo, formInput?.learning_file],
  );

  const handleVideoChange = async (event) => {
    setIsLoading(true);

    const file = event.target.files[0];

    if (file) {
      const videoObjectUrl = URL.createObjectURL(file);

      try {
        const learnVideo = await tusUtils.createUploadData(file, onTusResume, {
          Authorization: `Bearer ${auth.idToken}`,
        });
        setFileTus.setLearnVideoTus(learnVideo);
      } catch (e) {
        console.error(e);
      }

      setFormInput((prevFormInput) => ({
        ...prevFormInput,
        learn_video: file,
      }));
      setIsVideo(file.type.includes('video'));
      setSelectedVideo(file);
      setPreviewVideo(videoObjectUrl);

      setTimeout(() => {
        setIsVideoPlayerReloaded(true);
        setVideoPlayerId(uuid());
      }, 1000);
    }

    setIsLoading(false);
  };

  const handleDelete = async () => {
    setSelectedVideo(null);
    setPreviewVideo('');
    setIsVideo(false);
    setFileTus.setLearnVideoTus(null);
    setFormInput((prevFormInput) => {
      const updatedFormInput = { ...prevFormInput };
      delete updatedFormInput.learn_video;
      delete updatedFormInput.learning_file;
      return updatedFormInput;
    });
  };

  // const handleFileChange = async (event) => {
  //   setIsLoading(true);

  //   const file = event.target.files[0];

  //   if (file) {
  //     try {
  //       const eventImage = await tusUtils.createUploadData(file, onTusResume, {
  //         Authorization: `Bearer ${auth.idToken}`,
  //       });
  //       setFileTus.setEventImageTus(eventImage);
  //     } catch (e) {
  //       console.error(e);
  //     }

  //     setSelectedFile(file);
  //     setPreviewUrl(URL.createObjectURL(file));
  //     setFormInput((prevFormInput) => ({ ...prevFormInput, image: file }));
  //   }

  //   setIsLoading(false);
  // };

  const handleFileChange = (event, field = 'image') => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    const objectUrl = URL.createObjectURL(file);
    setSelectedFile(file);
    setPreviewUrl(objectUrl);

    setFormInput((prev) => ({
      ...prev,
      [field]: file,
    }));
  };

  const handleCheckboxChange = async (event) => {
    setFormInput({ ...formInput, is_hybrid: event.target.checked });
  };

  const handleCheckboxChanges = async (event) => {
    setFormInput({ ...formInput, is_cme: event.target.checked });
  };

  React.useEffect(() => {
    if (setIsLoading) {
      if (!auth?.idToken) {
        setIsLoading(true);
      } else if (auth?.idToken) {
        setIsLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, setIsLoading]);

  React.useEffect(() => {
    if (videoPlayerRef?.current && !isReloadVideoPlayer) {
      if (!isVideoPlayerReloaded) {
        setIsVideoPlayerReloaded(true);
        setIsReloadVideoPlayer(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoPlayerRef?.current, isReloadVideoPlayer]);

  React.useEffect(() => {
    if (isReloadVideoPlayer) {
      setVideoPlayerId(uuid());
      setIsReloadVideoPlayer(false);
    }
  }, [isReloadVideoPlayer]);

  const handleType = (value, isChecked) => {
    if (formInput) {
      const eventTypes = Array.isArray(formInput.roles) ? formInput.roles : [];

      const updatedSelection = isChecked
        ? [...eventTypes, value]
        : eventTypes.filter((selected) => selected !== value);

      setFormInput({ ...formInput, roles: updatedSelection });
    } else {
      console.error('formInput is not defined');
    }
  };

  const [getItem, setGetItem] = useState();

  useEffect(() => {
    axios
      .get(`${url}`, {
        headers: {
          Authorization: `Bearer ${auth.idToken}`,
        },
      })
      .then((response) => {
        setGetItem(response.data);
      })
      .catch((error) => {
        console.log(error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className='px-6 py-4'>
        <label className='font-poppins text-md block py-4 font-bold leading-6 text-gray-600'>
          Event Image (Optional)
        </label>
        <Box
          sx={{
            width: '173px',
            height: '95px',

            overflow: 'hidden',
          }}
          className='flex justify-center rounded-xl bg-gray-300'
        >
          <input
            type='file'
            accept='image/*'
            onChange={(e) => handleFileChange(e, 'event_image')}
            style={{ display: 'none' }}
            id='upload-file-input'
          />

          {selectedFile || previewUrl || formInput?.event_image ? (
            <div>
              <img
                src={previewUrl || formInput?.event_image}
                alt=''
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
          ) : null}
        </Box>
        <div className='py-5'>
          <label htmlFor='upload-file-input'>
            <Button
              variant='contained'
              sx={{ textTransform: 'none' }}
              component='span'
              className='bg-cyan-600 from-cyan-500 to-cyan-600 text-xs font-bold'
            >
              Upload Image
            </Button>
          </label>
        </div>
        <div className='sm:col-span-6'>
          <div className='text-xs  text-gray-300'>*size 375 x 190</div>
        </div>
      </div>
      <div className='mt-3 grid grid-cols-1 gap-x-6 gap-y-6 px-5 sm:grid-cols-6'>
        <div className='sm:col-span-6'>
          <label className='text-md block font-bold leading-6 text-gray-600 after:ml-0.5 after:text-gray-600 after:content-["*"]'>
            Event Title
          </label>
          <div className='font-poppins mt-2 font-medium'>
            <input
              placeholder='Enter Event Title'
              id='title'
              type='text'
              className='font-poppins  block w-full rounded-md border-0 bg-gray-100 py-3 text-gray-600 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6'
              value={
                formInput
                  ? formInput.event_title || ''
                  : update.event_title || ''
              }
              onChange={(e) =>
                formInput
                  ? setFormInput({ ...formInput, event_title: e.target.value })
                  : setUpdate({ ...update, event_title: e.target.value })
              }
            />
          </div>
        </div>
      </div>
      <div className='mt-3 grid grid-cols-1 gap-x-6 gap-y-6 py-2 px-5 sm:grid-cols-6'>
        <div className='sm:col-span-6'>
          <label className='text-md block font-bold leading-6 text-gray-600'>
            Event Description
          </label>
          <div className='mt-2 font-medium'>
            <textarea
              placeholder='Enter Event Description'
              rows='4'
              className='font-poppins block w-full rounded-md border-0 bg-gray-100 py-1.5 text-gray-600 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6'
              value={
                formInput ? formInput.event_desc || '' : update.event_desc || ''
              }
              onChange={(e) =>
                formInput
                  ? setFormInput({ ...formInput, event_desc: e.target.value })
                  : setUpdate({ ...update, event_desc: e.target.value })
              }
            />
          </div>
        </div>
      </div>
      <div className='mt-3 grid grid-cols-1 gap-x-6 gap-y-6 px-5 sm:grid-cols-6'>
        <div className='sm:col-span-6'>
          <label className='text-md block font-bold leading-6 text-gray-600'>
            Link Event to
          </label>
          <div className='mt-2 font-medium'>
            <input
              id='link'
              placeholder='https://'
              type='text'
              className='font-poppins focus:ring- -600 block w-full rounded-md border-0 bg-gray-100 py-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-1
              focus:ring-inset sm:text-sm sm:leading-6'
              value={
                formInput ? formInput.event_link || '' : update.event_link || ''
              }
              onChange={(e) =>
                formInput
                  ? setFormInput({ ...formInput, event_link: e.target.value })
                  : setUpdate({ ...update, event_link: e.target.value })
              }
            />
          </div>
        </div>
      </div>
      <div className='mt-3 grid grid-cols-1 gap-x-6 gap-y-6 px-5 sm:grid-cols-6'>
        <div className='py-2 sm:col-span-6'>
          <div className='flex justify-between'>
            <label className='text-md block font-bold leading-6 text-gray-600'>
              Live Stream Link
            </label>
            <div className='flex justify-between'>
              <div className='text-md px-2 font-bold text-gray-600'>
                Hybrid event
              </div>
              <Checkbox
                className='py-0 px-0'
                checked={Boolean(formInput?.is_hybrid)}
                onChange={handleCheckboxChange}
                style={{ color: '#1788b9' }}
              />
            </div>
          </div>
          <div className='mt-2 font-medium'>
            <input
              id='link'
              placeholder='https://'
              type='text'
              className='font-poppins focus:ring- -600 block w-full rounded-md border-0 bg-gray-100 py-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-1
              focus:ring-inset sm:text-sm sm:leading-6'
              value={
                formInput
                  ? formInput.live_stream || ''
                  : update.live_stream || ''
              }
              onChange={(e) =>
                formInput
                  ? setFormInput({ ...formInput, live_stream: e.target.value })
                  : setUpdate({ ...update, live_stream: e.target.value })
              }
            />
          </div>
        </div>
      </div>

      <div className='mt-3 grid grid-cols-1 gap-x-6 gap-y-6 px-5 sm:grid-cols-6'>
        <div className='py-2 sm:col-span-6'>
          <label className='text-md block font-bold leading-6 text-gray-600 after:ml-0.5 after:text-gray-600 after:content-["*"]'>
            Select Role
          </label>
          <CheckboxGroup
            selectedValues={formInput ? formInput.roles : update.roles}
            onChange={handleType}
          />
        </div>
      </div>

      <div className='mt-3 grid grid-cols-1 gap-x-6 gap-y-6 px-5 sm:grid-cols-6'>
        <div className='py-2 sm:col-span-6'>
          <label className='text-md block font-bold leading-6 text-gray-600'>
            Select Event Tag
          </label>
          <div
            className='mt-2'
            style={{
              overflowY: 'auto',
              maxHeight: '150px',
            }}
          >
            <select
              style={{ height: '50px', width: '250px' }}
              onChange={(e) =>
                formInput
                  ? setFormInput({ ...formInput, event_tags: e.target.value })
                  : setUpdate({ ...update, event_tags: e.target.value })
              }
              value={formInput ? formInput.event_tags : update.event_tags}
              className='font-poppins block w-full rounded-md border-0 bg-gray-100 px-2 py-3.5 text-gray-600 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xs sm:text-sm sm:leading-6'
              disabled={
                formInput?.roles === 'Clinic Assistants' ||
                (update && update.roles === 'Clinic Assistants')
              }
            >
              <option value={''}>Select One</option>
              {getItem?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.item}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className='px-6 py-4'>
        <label className='font-poppins text-md block py-4 font-bold leading-6 text-gray-600'>
          Video Preview Event (Optional)
        </label>
        <div>
          <input
            type='file'
            accept='video/*'
            onChange={handleVideoChange}
            style={{ display: 'none' }}
            id='upload-video-input'
          />
          <label htmlFor='upload-video-input'>
            <Button
              variant='contained'
              sx={{ textTransform: 'none' }}
              component='span'
              className='bg-cyan-600 from-cyan-500 to-cyan-600 font-bold'
            >
              Upload Video
            </Button>
          </label>
          {showVideoPreview ? (
            <div className='relative mt-8 w-[200px]'>
              <ReactPlayer
                key={videoPlayerId}
                ref={(player) => {
                  videoPlayerRef.current = player;
                }}
                url={videoUrl}
                controls
                width='200px'
                height='auto'
                style={{
                  display: showVideoPlayer ? 'block' : 'none',
                  marginTop: 20,
                }}
              />
              <img
                src={previewVideo}
                alt='Preview'
                style={{
                  display: !showVideoPlayer ? 'block' : 'none',
                  width: '200px',
                  marginTop: 20,
                }}
              />
              <div className='group absolute top-1 right-1 py-2 pt-1'>
                <div className='absolute -right-1/2 -top-1/2 h-10 w-10 translate-x-1/2 -translate-y-1/2 transform rounded-full bg-gray-300 transition-all duration-300 group-hover:bg-white group-hover:shadow-lg'>
                  <Icon
                    onClick={handleDelete}
                    icon='clarity:remove-solid'
                    color='#FF5555'
                    width='28'
                    className='h-10 w-10 scale-100 transform rounded-full transition-transform group-hover:scale-110'
                  />
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className='px-6 py-4'>
        <div className='font-poppins text-md block font-bold leading-6 text-gray-600'>
          Eligible for CME points?
        </div>
      </div>
      <div className='px-4'>
        <div className='flex justify-start'>
          <div className='flex justify-between'>
            <Checkbox
              className='py-0'
              checked={Boolean(formInput?.is_cme)}
              onChange={handleCheckboxChanges}
              style={{ color: '#1788b9' }}
            />
            <div className=' text-md py-2 text-gray-600'>Yes</div>
          </div>
        </div>
      </div>
    </>
  );
}
